import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, FileText, MessageCircleQuestion } from "lucide-react";
import { api } from "@/lib/api";
import { MF } from "@/lib/marketingFoundations";
import { PillarsExtra, TextExtra, UvpCheck, useRootsExtras } from "@/components/RootsExtras";
import { ResourceModal } from "@/pages/member/Library";

const HINTS = {
  cpg: { not_for: "e.g. Not for the bargain hunter, or the shopper who just wants the familiar brand.", uvp: "e.g. The only ambient hot sauce made with UK-grown chillies…", emotion: "e.g. Proud of their choice, like they've discovered something.", pillar: "e.g. How our product is made" },
  hospitality: { not_for: "e.g. Not for the hen party looking for cocktails and a DJ.", uvp: "e.g. The only fully natural wine bar in the city…", emotion: "e.g. Relaxed, properly looked after, like they're in on a secret.", pillar: "e.g. Behind the scenes" },
};
const FIELD = { "not-for": "not_for", uvp: "uvp", emotion: "emotion", pillars: "pillars" };

export const foundationsDone = (x) => (x ? [x.not_for, x.uvp, x.emotion].filter(Boolean).length + ((x.pillars || []).length >= 3 ? 1 : 0) : 0);

const AskLink = ({ q, id }) => (
  <Link to={`/app/ask?q=${encodeURIComponent(q)}`} data-testid={id} className="inline-flex items-center gap-1 font-medium text-pinkb hover:underline">
    <MessageCircleQuestion className="h-3.5 w-3.5 shrink-0" />"{q}"
  </Link>
);

const Para = ({ p, id, worksheet, onOpen }) => {
  if (typeof p === "string") return <p>{p}</p>;
  if (p.example) return <p className="rounded-lg bg-secondary px-4 py-3 italic text-navy">{p.example}</p>;
  if (p.template) return <p>{p.template} {worksheet
    ? <button type="button" onClick={() => onOpen(worksheet)} data-testid={`${id}-template`} className="inline-flex items-center gap-1 font-medium text-pinkb hover:underline"><FileText className="h-3.5 w-3.5" />{p.label}</button>
    : <span className="text-xs font-mono uppercase text-muted-foreground" data-testid={`${id}-template`}>Worksheet coming soon</span>}</p>;
  return <p>{p.pre}<AskLink q={p.q} id={`${id}-ask`} />{p.post}</p>;
};

const Editor = ({ k, v, extras, save }) => {
  const h = HINTS[v];
  if (k === "pillars") return <PillarsExtra extras={extras} save={save} placeholder={h.pillar} />;
  if (k === "uvp") return <TextExtra extras={extras} save={save} field="uvp" title="My UVP in one sentence" placeholder={h.uvp} max={300}>{(text) => <UvpCheck uvp={text} />}</TextExtra>;
  if (k === "emotion") return <TextExtra extras={extras} save={save} field="emotion" title="The feeling I want customers to have" placeholder={h.emotion} max={500} />;
  return <TextExtra extras={extras} save={save} field="not_for" title="Who we're not for" placeholder={h.not_for} max={1000} rows={3} />;
};

export const RootsFoundationsForm = ({ audience }) => {
  const v = audience === "hospitality" ? "hospitality" : "cpg";
  const [extras, save] = useRootsExtras();
  const [worksheet, setWorksheet] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => { api.get("/resources").then((r) => setWorksheet(r.data.find((x) => /content pillar/i.test(x.title)) || null)).catch(() => {}); }, []);
  if (!extras) return null;
  const done = foundationsDone(extras);
  return (
    <div className="space-y-4 text-sm leading-relaxed" data-testid="roots-foundations-form">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-muted-foreground max-w-xl">Your profile gives you the foundations. These four add to them. Answers save to your Roots Profile, and Lindsay's answers use them.</p>
        <span className={`rounded-full px-3 py-1 font-mono text-xs ${done === 4 ? "bg-leaf/20 text-navy" : "bg-secondary text-navy"}`} data-testid="roots-foundations-progress">{done}/4 done</span>
      </div>
      <ol className="space-y-4">{MF[v].add.map((a, i) => {
        const filled = a.key === "pillars" ? extras.pillars.length >= 3 : !!extras[FIELD[a.key]];
        return (
          <li key={a.key} className={`rounded-xl border p-5 ${filled ? "border-leaf/50 bg-leaf/5" : "border-border bg-white"}`} data-testid={`roots-q-${a.key}`}>
            <h4 className="flex items-center gap-2 font-serif uppercase text-lg text-navy leading-tight">
              {filled ? <CheckCircle2 className="h-5 w-5 text-leaf shrink-0" /> : <span className="font-mono text-sm text-pinkb">{i + 1}.</span>}{a.title}
            </h4>
            <div className="mt-3 space-y-3">
              {a.paras.map((p, j) => <Para key={j} p={p} id={`roots-q-${a.key}-${j}`} worksheet={worksheet} onOpen={setOpen} />)}
              <Editor k={a.key} v={v} extras={extras} save={save} />
            </div>
          </li>
        );
      })}</ol>
      <ResourceModal item={open} onClose={() => setOpen(null)} />
    </div>
  );
};
